import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { fetchInquiries, loginAdmin } from "@/lib/api";

const TOKEN_KEY = "dh_admin_token";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const apiError = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return fallback;
};

const Row = ({ label, value }) =>
  value ? (
    <p className="text-sm font-light text-sand/90">
      <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">{label}</span>
      {value}
    </p>
  ) : null;

const BookingCard = ({ b }) => (
  <article data-testid={`inquiry-booking-${b.id}`} className="border border-white/[0.08] bg-charcoal-900/60 p-6">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-serif text-xl font-light text-sand-bright">{b.full_name}</h3>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{fmt(b.created_at)}</span>
    </div>
    <div className="mt-4 grid gap-2 sm:grid-cols-2">
      <Row label="Email" value={b.email} />
      <Row label="Phone" value={b.phone} />
      <Row label="Org / Event" value={b.organization} />
      <Row label="Date" value={b.event_date} />
      <Row label="Location" value={b.location} />
      <Row label="Type" value={b.event_type} />
      <Row label="Audience" value={b.audience_size} />
    </div>
    <p className="mt-4 border-t border-white/[0.06] pt-4 text-sm font-light leading-relaxed text-mist">{b.message}</p>
  </article>
);

const ContactCard = ({ c }) => (
  <article data-testid={`inquiry-contact-${c.id}`} className="border border-white/[0.08] bg-charcoal-900/60 p-6">
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-serif text-xl font-light text-sand-bright">{c.name}</h3>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{fmt(c.created_at)}</span>
    </div>
    <div className="mt-4 grid gap-2 sm:grid-cols-2">
      <Row label="Email" value={c.email} />
      <Row label="Subject" value={c.subject} />
    </div>
    <p className="mt-4 border-t border-white/[0.06] pt-4 text-sm font-light leading-relaxed text-mist">{c.message}</p>
  </article>
);

const fieldClass =
  "w-full border border-white/10 bg-charcoal-900/80 px-4 py-3 text-sm font-light text-sand placeholder:text-mist/50 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors duration-300";

const LoginGate = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const { data } = await loginAdmin(email, password);
      onLogin(data.token);
    } catch (err) {
      setError(apiError(err, "Sign-in failed. Please try again."));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-6 pb-28" data-testid="admin-login-gate">
      <form onSubmit={submit} className="border border-white/[0.08] bg-charcoal-900/60 p-8 sm:p-10">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Authorized access only</p>
        <h2 className="mt-3 font-serif text-2xl font-light text-sand-bright">Administrator sign in</h2>
        <div className="mt-8 space-y-5">
          <div>
            <label htmlFor="admin-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
              Email
            </label>
            <Input id="admin-email" type="email" data-testid="admin-login-email" required value={email} onChange={(e) => setEmail(e.target.value)} className={fieldClass} placeholder="admin email" autoComplete="username" />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
              Password
            </label>
            <Input id="admin-password" type="password" data-testid="admin-login-password" required value={password} onChange={(e) => setPassword(e.target.value)} className={fieldClass} placeholder="password" autoComplete="current-password" />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="border border-red-900/50 bg-red-950/40 px-4 py-3 text-sm font-light text-red-300">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={sending}
            className="w-full bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal-950 transition-colors duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? "Signing in…" : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default function Inquiries() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("booking");
  const [data, setData] = useState({ bookings: [], contacts: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback((t) => {
    setLoading(true);
    setError(false);
    fetchInquiries(t)
      .then((res) => setData(res.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.removeItem(TOKEN_KEY);
          setToken("");
          toast.error("Session expired", { description: "Please sign in again." });
        } else {
          setError(true);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const handleLogin = (t) => {
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData({ bookings: [], contacts: [] });
  };

  const list = tab === "booking" ? data.bookings : data.contacts;

  return (
    <>
      <Seo title="Submissions — Derrick Hall" siteName="Derrick Hall" description="Saved booking and contact submissions." />
      <PageHeader
        testId="inquiries-header"
        eyebrow="Administration"
        title="Saved Submissions"
        description="Booking inquiries and contact messages sent through this site. Restricted to the site administrator."
      />

      {!token ? (
        <LoginGate onLogin={handleLogin} />
      ) : (
        <div className="mx-auto max-w-5xl px-6 pb-28" data-testid="inquiries-page">
          <div className="mb-10 flex flex-wrap items-center gap-3">
            {[
              { key: "booking", label: `Booking (${data.bookings.length})`, testId: "inquiries-tab-booking" },
              { key: "contact", label: `Contact (${data.contacts.length})`, testId: "inquiries-tab-contact" },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={t.testId}
                onClick={() => setTab(t.key)}
                className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  tab === t.key
                    ? "border-gold/60 bg-gold/10 text-gold"
                    : "border-white/10 text-mist hover:border-gold/30 hover:text-sand"
                }`}
              >
                {t.label}
              </button>
            ))}
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="ml-auto flex items-center gap-2 border border-white/10 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-mist transition-colors duration-300 hover:border-gold/30 hover:text-sand"
            >
              <LogOut size={13} />
              Sign out
            </button>
          </div>

          {loading && <p className="text-sm text-mist" data-testid="inquiries-loading">Loading submissions…</p>}
          {error && <p className="text-sm text-mist" data-testid="inquiries-error">Could not load submissions right now.</p>}
          {!loading && !error && list.length === 0 && (
            <p className="border border-white/[0.08] bg-charcoal-900/60 p-8 text-sm font-light text-mist" data-testid="inquiries-empty">
              No {tab} submissions yet.
            </p>
          )}
          <div className="space-y-6">
            {tab === "booking"
              ? data.bookings.map((b) => <BookingCard key={b.id} b={b} />)
              : data.contacts.map((c) => <ContactCard key={c.id} c={c} />)}
          </div>
        </div>
      )}
    </>
  );
}
